import React, { useState } from 'react';
import axios from 'axios';

const emptyForm = {
    name: '',
    email: '',
    phone: '',
    program: '',
    cohort: '',
    total_work_experience: '',
    current_ctc: '',
    expected_ctc: '',
    cvFiles: [],
    videoFiles: [],
};

const Loader = () => (
    <div className="loader-overlay d-flex justify-content-center align-items-center">
        <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
        </div>
    </div>
);

const LeadsenseJobMulti = () => {
    const [forms, setForms] = useState([{ ...emptyForm }]);
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);

    const updateFormField = (index, key, value) => {
        const updated = [...forms];
        updated[index][key] = value;
        setForms(updated);
    };

    const updateFileField = (index, key, files) => {
        const updated = [...forms];
        updated[index][key] = Array.from(files);
        setForms(updated);
    };

    const addApplicant = () => {
        setForms([...forms, { ...emptyForm }]);
    };

    const removeApplicant = (index) => {
        const updated = forms.filter((_, i) => i !== index);
        setForms(updated);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage('');

        try {
            for (const [i, form] of forms.entries()) {
                const formData = new FormData();

                const requiredFields = [
                    'name',
                    'email',
                    'phone',
                    'program',
                    'cohort',
                    'total_work_experience',
                    'current_ctc',
                    'expected_ctc',
                ];

                for (const field of requiredFields) {
                    if (!form[field]) {
                        throw new Error(`Applicant #${i + 1}: "${field}" is required.`);
                    }
                    formData.append(field, form[field]);
                }

                if (form.cvFiles.length === 0 || form.videoFiles.length === 0) {
                    throw new Error(`Applicant #${i + 1}: CV and Video files are required.`);
                }

                form.cvFiles.forEach((file) => {
                    formData.append('cv', file);
                });

                form.videoFiles.forEach((file) => {
                    formData.append('video', file);
                });

                await axios.post(
                    'https://api.theleadsense.com/api/v1/test/placement',
                    formData,
                    { headers: { 'Content-Type': 'multipart/form-data' } }
                );
            }

            setMessage('✅ All applications submitted successfully!');
            setForms([{ ...emptyForm }]);
        } catch (err) {
            console.error(err);
            setMessage(`❌ Submission failed: ${err.response?.data?.message || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container py-5">
            {loading && <Loader />}
            <style>{`
                .loader-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100vw;
                    height: 100vh;
                    background: rgba(255, 255, 255, 0.7);
                    z-index: 1050;
                }
            `}</style>

            <form onSubmit={handleSubmit}>
                {forms.map((form, index) => (
                    <div key={index} className="card mb-4 shadow-sm">
                        <div className="card-header d-flex justify-content-between align-items-center bg-primary text-white">
                            <h5 className="mb-0">Applicant #{index + 1}</h5>
                            {forms.length > 1 && (
                                <button
                                    type="button"
                                    className="btn btn-sm btn-danger"
                                    onClick={() => removeApplicant(index)}
                                >
                                    Remove
                                </button>
                            )}
                        </div>
                        <div className="card-body">
                            <div className="row g-3">
                                {[
                                    ['name', 'Full Name'],
                                    ['email', 'Email Address'],
                                    ['phone', 'Phone Number'],
                                    ['program', 'Program'],
                                    ['cohort', 'Cohort'],
                                    ['total_work_experience', 'Work Experience (years)'],
                                    ['current_ctc', 'Current CTC'],
                                    ['expected_ctc', 'Expected CTC'],
                                ].map(([key, label]) => (
                                    <div className="col-md-6" key={key}>
                                        <label className="form-label fw-medium">{label}</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={form[key]}
                                            onChange={(e) => updateFormField(index, key, e.target.value)}
                                            required
                                        />
                                    </div>
                                ))}

                                <div className="col-md-6">
                                    <label className="form-label fw-medium">Upload CV(s)</label>
                                    <input
                                        type="file"
                                        className="form-control"
                                        accept=".pdf,.doc,.docx"
                                        multiple
                                        onChange={(e) => updateFileField(index, 'cvFiles', e.target.files)}
                                        required
                                    />
                                    {form.cvFiles.length > 0 && (
                                        <ul className="mt-2 small text-muted">
                                            {form.cvFiles.map((file, i) => (
                                                <li key={i}>{file.name}</li>
                                            ))}
                                        </ul>
                                    )}
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label fw-medium">Upload Video(s)</label>
                                    <input
                                        type="file"
                                        className="form-control"
                                        accept="video/*"
                                        multiple
                                        onChange={(e) => updateFileField(index, 'videoFiles', e.target.files)}
                                        required
                                    />
                                    {form.videoFiles.length > 0 && (
                                        <ul className="mt-2 small text-muted">
                                            {form.videoFiles.map((file, i) => (
                                                <li key={i}>{file.name}</li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}

                <div className="d-flex justify-content-between align-items-center mt-4">
                    <button type="button" className="btn btn-outline-secondary" onClick={addApplicant}>
                        ➕ Add Another Applicant
                    </button>
                    <button type="submit" className="btn btn-success" disabled={loading}>
                        {loading ? 'Submitting...' : 'Submit All Applications'}
                    </button>
                </div>
            </form>

            {message && (
                <div
                    className={`alert mt-4 text-center ${
                        message.includes('✅') ? 'alert-success' : 'alert-danger'
                    }`}
                    role="alert"
                >
                    {message}
                </div>
            )}
        </div>
    );
};

export default LeadsenseJobMulti;
